import { useQueryClient } from '@tanstack/react-query';
import { ImagePlus, Trash2, Upload } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { toast } from 'sonner';
import { FormModal } from '@/components/shared/form-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, errorMessage } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useAssets, type Asset } from './types';

export interface ImageValue {
  assetId?: string | null;
  url?: string | null;
  alt?: string;
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif';

function Picker({ open, onOpenChange, onPick, purpose }: { open: boolean; onOpenChange: (v: boolean) => void; onPick: (a: Asset) => void; purpose: string }) {
  const qc = useQueryClient();
  const assets = useAssets();
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function send() {
    if (!file) return;
    if (!alt.trim()) return setError('Descreva a imagem para quem não consegue vê-la.');
    setBusy(true);
    setError(undefined);
    try {
      const fd = new FormData();
      fd.append('purpose', purpose);
      fd.append('alt', alt.trim());
      fd.append('file', file);
      const a = await api.post<Asset>('/assets', fd);
      await qc.invalidateQueries({ queryKey: ['assets'] });
      setFile(null);
      setAlt('');
      toast.success('Imagem enviada');
      onPick(a);
      onOpenChange(false);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <FormModal open={open} onOpenChange={onOpenChange} title="Escolher imagem" description="Envie uma foto nova ou use uma já enviada. Tudo é convertido para WebP." size="lg">
      <div className="grid gap-6">
        <div className="grid gap-3 rounded-lg border bg-muted/40 p-4">
          <input
            ref={input}
            id={`${id}-f`}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              setError(undefined);
              if (f && !ACCEPT.split(',').includes(f.type)) return setError('Use uma imagem JPG, PNG, WebP ou AVIF.');
              if (f && f.size > 8 * 1024 * 1024) return setError('A imagem passa de 8 MB.');
              setFile(f ?? null);
            }}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Button type="button" variant="outline" size="sm" onClick={() => input.current?.click()}>
              <Upload aria-hidden /> {file ? 'Trocar arquivo' : 'Enviar foto nova'}
            </Button>
            {file && <span className="truncate text-sm text-muted-foreground">{file.name}</span>}
          </div>
          {file && (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor={`${id}-alt`}>Texto alternativo (obrigatório)</Label>
                <Input id={`${id}-alt`} value={alt} maxLength={200} onChange={(e) => setAlt(e.target.value)} placeholder="Ex.: Barbeiro finalizando um degradê" />
              </div>
              <div>
                <Button type="button" size="sm" onClick={send} disabled={busy}>
                  {busy ? 'Enviando…' : 'Enviar e usar'}
                </Button>
              </div>
            </>
          )}
          {error && (
            <p role="alert" className="text-xs text-destructive">
              {error}
            </p>
          )}
        </div>
        <div>
          <p className="mb-2 text-sm font-medium">Imagens enviadas</p>
          {assets.isPending && <p className="text-sm text-muted-foreground">Carregando…</p>}
          {assets.data?.length === 0 && <p className="text-sm text-muted-foreground">Nenhuma imagem enviada ainda.</p>}
          <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {assets.data?.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  className="group block w-full overflow-hidden rounded-md border text-left focus-visible:ring-2 focus-visible:ring-ring"
                  onClick={() => {
                    onPick(a);
                    onOpenChange(false);
                  }}
                >
                  <img src={a.url} alt={a.alt} loading="lazy" width={a.width ?? 160} height={a.height ?? 120} className="aspect-square w-full object-cover transition-opacity group-hover:opacity-80" />
                  <span className="block truncate px-2 py-1 text-xs text-muted-foreground">{a.alt || 'Sem descrição'}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </FormModal>
  );
}

/** Seleção de imagem da biblioteca (ou envio). `withAlt` expõe o texto alternativo editável. */
export function ImageField({
  label,
  value,
  onChange,
  withAlt = true,
  purpose = 'site',
  className,
}: {
  label: string;
  value: ImageValue | null | undefined;
  onChange: (v: ImageValue | null) => void;
  withAlt?: boolean;
  purpose?: 'site' | 'logo' | 'gallery';
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const assets = useAssets();
  const id = useId();
  const url = value?.assetId ? assets.data?.find((a) => a.id === value.assetId)?.url : (value?.url ?? undefined);
  const has = Boolean(value?.assetId || value?.url);
  return (
    <div className={cn('grid gap-2', className)}>
      <Label htmlFor={`${id}-btn`}>{label}</Label>
      <div className="flex items-start gap-3">
        <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted">
          {url ? <img src={url} alt="" className="size-full object-cover" /> : <ImagePlus className="size-5 text-muted-foreground" aria-hidden />}
        </div>
        <div className="grid min-w-0 flex-1 gap-2">
          <div className="flex flex-wrap gap-2">
            <Button id={`${id}-btn`} type="button" variant="outline" size="sm" onClick={() => setOpen(true)}>
              <ImagePlus aria-hidden /> {has ? 'Trocar' : 'Escolher imagem'}
            </Button>
            {has && (
              <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
                <Trash2 aria-hidden /> Remover
              </Button>
            )}
          </div>
          {has && withAlt && (
            <Input aria-label={`${label}: texto alternativo`} value={value?.alt ?? ''} maxLength={200} placeholder="Texto alternativo (descreva a imagem)" onChange={(e) => onChange({ ...value, alt: e.target.value })} />
          )}
        </div>
      </div>
      <Picker
        open={open}
        onOpenChange={setOpen}
        purpose={purpose}
        onPick={(a) => onChange({ assetId: a.id, alt: value?.alt || a.alt })}
      />
    </div>
  );
}
